import React from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

// Daisy's backstory
const DAISY_BACKSTORY = {
  name: "Daisy",
  realName: "Daisy Mae Thompson",
  occupation: "VeriWatch Command Center Dispatcher & Critical Intel Analyst",
  bio: `Former 911 dispatcher with 15 years on the line, Daisy has talked people through 
every emergency imaginable - from house fires to active shooters to natural disasters. 
Her calm, steady voice has saved more lives than she can count.

She met Tank at a HAM radio meetup in 2019. What started as friendly frequency banter 
turned into something more when they discovered their shared passion for preparedness. 
Now she runs comms for their local prepper network from their homestead's command center.

When Tank's on the road, Daisy monitors everything - OSINT feeds, weather patterns, 
supply chain disruptions, grid status. If something's happening, she knows about it 
before it hits the news.

"In an emergency, panic kills. Information saves. That's why I'm here."

Specialties: Emergency dispatch protocols, crisis communication, medical triage guidance,
OSINT analysis, and multi-source intelligence correlation.`,
  funFacts: [
    "Can type 120 WPM while simultaneously monitoring 6 radio channels",
    "Certified EMT - keeps a full trauma kit in every room of their house",
    "Her callsign is 'Homebase' on the prepper network",
    "Has memorized emergency frequencies for every state they travel through"
  ]
};

const DaisyAvatar = ({ 
  size = 'md', 
  showTooltip = true, 
  className = '',
  showName = false 
}) => {
  const sizeClasses = {
    xs: 'w-6 h-6',
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16',
    '2xl': 'w-24 h-24'
  };

  const avatar = (
    <div className={`relative ${className}`}>
      <img 
        src="/images/daisy.png" 
        alt="Daisy - VeriWatch Dispatcher" 
        className={`${sizeClasses[size]} rounded-full object-cover border-2 border-amber-500/50 shadow-lg shadow-amber-500/20`}
      />
      <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-slate-900" />
      {showName && (
        <span className="ml-2 text-sm font-medium text-amber-400">Daisy</span>
      )}
    </div>
  );

  if (!showTooltip) {
    return avatar;
  }

  return (
    <TooltipProvider>
      <Tooltip delayDuration={300}>
        <TooltipTrigger asChild>
          <div className="cursor-help inline-flex items-center">
            {avatar}
          </div>
        </TooltipTrigger>
        <TooltipContent 
          side="bottom" 
          className="max-w-md p-4 bg-slate-900 border border-amber-500/30 text-slate-200 shadow-xl"
        >
          <div className="space-y-3">
            <div className="flex items-center gap-3 border-b border-slate-700 pb-3">
              <img 
                src="/images/daisy.png" 
                alt="Daisy" 
                className="w-14 h-14 rounded-full border-2 border-amber-500"
              />
              <div>
                <h4 className="font-bold text-amber-400 text-lg">{DAISY_BACKSTORY.name}</h4>
                <p className="text-xs text-slate-400">{DAISY_BACKSTORY.realName}</p>
                <p className="text-xs text-teal-400">{DAISY_BACKSTORY.occupation}</p>
              </div>
            </div>
            
            <p className="text-sm text-slate-300 whitespace-pre-line leading-relaxed">
              {DAISY_BACKSTORY.bio}
            </p>
            
            <div className="pt-2 border-t border-slate-700">
              <p className="text-xs text-amber-500 font-semibold mb-1">Fun Facts:</p>
              <ul className="text-xs text-slate-400 space-y-1">
                {DAISY_BACKSTORY.funFacts.map((fact, i) => (
                  <li key={i} className="flex items-start gap-1">
                    <span className="text-teal-500">•</span> {fact}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Export both the component and the backstory for use elsewhere
export { DAISY_BACKSTORY };
export default DaisyAvatar;
